import React, { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, Building2, BadgeCheck, Plus, ChevronRight, Phone, Mail, AlertTriangle,
  Search, Shield, FileSpreadsheet, CalendarDays,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Input, Spinner, EmptyState,
} from "@/components/ui";
import { fmtDate, daysFromNow, urgencyFromDays } from "@/lib/dates";
import { exportEmployeesXlsx } from "./exportEmployees";   // 8 Oct 2026 — the clean list for client systems

// =============================================================================
// People — employees + subcontractor companies
// =============================================================================

// =============================================================================
// James, 22 Aug 2026, on this screen: "must be old and not used - full review,
// then clean the nav up, nest screens where you think it makes sense, less like
// a rat warren of building code and more like a polished web portal."
//
// The review: three tabs, two of them duplicates of better screens.
//   * Employees - the only plain employee directory in the app, and everything
//     in HR points here. KEEP, as the whole screen.
//   * Subbies - already declared itself a leftover (T-381: "the hub is the real
//     front door"). Gone; the button to the Subbie Hub stays.
//   * Licences - a strictly worse copy of /hr/licences: no drill-through, no
//     verify. Gone; a link to Licence oversight stays.
// One screen, one job: find a person, open their file. The tabs were the rat
// warren.
// =============================================================================
export default function PeopleScreen() {
  const user = useRequireAuth();

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Employees</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Everyone on the books — click a person to open their file.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasRole(user, "manager") && (
            <>
              <ExportButton />
              <Link to="/subbies">
                <Button variant="secondary" icon={Shield}>Subbie Hub</Button>
              </Link>
              <Link to="/hr/licences">
                <Button variant="secondary" icon={BadgeCheck}>Licences</Button>
              </Link>
              <Link to="/people/new">
                <Button variant="primary" icon={Plus}>New employee</Button>
              </Link>
            </>
          )}
        </div>
      </div>

      <EmployeesTab canEdit={hasRole(user, "manager")} />
    </div>
  );
}

// -----------------------------------------------------------------------------
function EmployeesTab({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  // 3 Nov 2026 — James: "not all users are going to be scheduled and it creates slop … Test Test is my field user account …
  // day to day I don't need to see he is free." The flag is employees.is_schedulable (mig 325; the HR view shows it from 499).
  // Filter by it here and flip it in the row — Dispatch, the calendar and the planning board all read the same flag.
  const [sched, setSched] = useState<"all" | "on" | "off">("all");
  const toggleSched = useMutation({
    mutationFn: async (v: { employee_id: string; on: boolean }) => { const { error } = await (supabase as any).from("employees").update({ is_schedulable: v.on }).eq("employee_id", v.employee_id); if (error) throw new Error(error.message); },
    onSuccess: () => { for (const k of ["employees", "dispatch_people", "schedule_people", "planning_people"]) qc.invalidateQueries({ queryKey: [k] }); },
  });

  const { data: employees = [], isLoading } = useQuery({
    queryKey: ["employees", showInactive],
    queryFn: async () => {
      let q = supabase
        .from("v_employees")
        .select(`
          employee_id, first_name, last_name, email, mobile, employment_type,
          is_active, lafha_eligible, is_schedulable,
          subcontractor_companies(legal_name, trading_name)
        `)
        .order("first_name");
      if (!showInactive) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  const filtered = employees.filter(e => {
    if (sched === "on" && !e.is_schedulable) return false;
    if (sched === "off" && e.is_schedulable) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return `${e.first_name} ${e.last_name}`.toLowerCase().includes(s)
        || (e.email ?? "").toLowerCase().includes(s);
  });

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 max-w-md">
          <Search size={14} className="text-text-muted" />
          <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
            className="bg-transparent outline-none text-text text-[13px] flex-1" />
        </div>
        <div className="inline-flex rounded-full border border-line overflow-hidden text-[12px]" title="On the schedule = shows on Dispatch, the calendar and the planning board">
          {([["all", "Everyone"], ["on", "On the schedule"], ["off", "Not scheduled"]] as const).map(([v, label]) => (
            <button key={v} type="button" onClick={() => setSched(v)} className={`px-3 py-1 ${sched === v ? "bg-accent/15 text-accent font-semibold" : "text-text-muted hover:text-text"}`}>{label}{v !== "all" ? ` · ${employees.filter(e => v === "on" ? e.is_schedulable : !e.is_schedulable).length}` : ""}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-[12px] text-text-muted">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="accent-accent" />
          Show inactive
        </label>
      </div>

      {isLoading ? <div className="py-10 flex justify-center"><Spinner/></div>
       : filtered.length === 0 ? <Card><EmptyState title="No employees match" icon={Users}/></Card>
       : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="border-b border-line">
                  {["Name", "Type", "Contact", "Company", "Schedule", "Active"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filtered.map((e, i) => (
                    <tr
                      key={e.employee_id}
                      onClick={() => navigate(`/people/${e.employee_id}`)}
                      className={`border-b border-line cursor-pointer hover:bg-ink-raised/40 transition-colors ${i % 2 ? "bg-ink/40" : ""} ${!e.is_active ? "opacity-50" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <div className="text-text">{e.first_name} {e.last_name}</div>
                        {e.lafha_eligible && <Pill label="LAFHA" tone="default" small />}
                      </td>
                      <td className="px-4 py-3 text-[12px]">{e.employment_type?.replace(/_/g, " ") ?? "—"}</td>
                      <td className="px-4 py-3 text-[12px]">
                        {e.email && <div className="flex items-center gap-1 text-text-muted"><Mail size={10}/> {e.email}</div>}
                        {e.mobile && <div className="flex items-center gap-1 text-text-muted font-mono"><Phone size={10}/> {e.mobile}</div>}
                      </td>
                      <td className="px-4 py-3 text-[12px]">{e.subcontractor_companies?.trading_name ?? e.subcontractor_companies?.legal_name ?? <span className="text-text-dim">Coretel</span>}</td>
                      <td className="px-4 py-3" onClick={ev => ev.stopPropagation()}>
                        {canEdit ? (
                          <button type="button" onClick={() => toggleSched.mutate({ employee_id: e.employee_id, on: !e.is_schedulable })} disabled={toggleSched.isPending}
                            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] ${e.is_schedulable ? "border-ok/50 bg-ok/10 text-ok" : "border-line text-text-dim hover:text-text"}`}
                            title={e.is_schedulable ? "On the schedule — click to take them off Dispatch and the boards (nothing already booked is touched)" : "Not scheduled — click to show them on Dispatch and the boards"}>
                            <CalendarDays size={11}/>{e.is_schedulable ? "on the schedule" : "not scheduled"}
                          </button>
                        ) : (e.is_schedulable ? <Pill label="on the schedule" tone="approved" small/> : <span className="text-[11px] text-text-dim">not scheduled</span>)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-between gap-2">
                          {e.is_active ? <Pill label="active" tone="approved" small /> : <Pill label="inactive" tone="default" small />}
                          <ChevronRight size={14} className="text-text-dim flex-shrink-0"/>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// 8 Oct 2026 — "Need a clean extra Excel report on employee list with details
// to copy / transfer into other client systems." Two sheets: Employees (contact,
// role, start date, DOB, address, next of kin) and Licences (ticket, number,
// expiry). Never TFN, bank, super or medical — see exportEmployees.ts.
// -----------------------------------------------------------------------------
function ExportButton() {
  const [busy, setBusy] = useState(false);
  async function run() {
    setBusy(true);
    try {
      const r = await exportEmployeesXlsx(window.confirm("Include inactive employees in the export?\n\nOK = everyone on the books, Cancel = active only."));
      alert(`Exported ${r.employees} employee${r.employees === 1 ? "" : "s"} and ${r.licences} licence${r.licences === 1 ? "" : "s"}.`);
    } catch (e: any) {
      alert(`Export failed: ${e?.message ?? e}`);
    } finally { setBusy(false); }
  }
  return (
    <Button variant="secondary" icon={FileSpreadsheet} onClick={run} disabled={busy}>
      {busy ? "Exporting…" : "Export to Excel"}
    </Button>
  );
}
